import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X } from "lucide-react";
import { api } from "@/lib/api";

export function IaReviewPanel({ clave, texto, onApply }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [decision, setDecision] = useState({});

  const run = async () => {
    setLoading(true); setRes(null); setDecision({});
    try {
      const { data } = await api.post(`/admin/plantillas/${clave}/revisar-ia`, { texto });
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo revisar con IA");
    } finally { setLoading(false); }
  };

  const aceptadas = (res?.sugerencias || []).filter((s) => decision[s.id] === "si");
  const aplicar = () => {
    let t = texto;
    aceptadas.forEach((s) => { t = t.replace(s.antes, s.despues); });
    onApply(t, aceptadas.length);
    setRes(null);
  };

  return (
    <div className="bm-card p-4 space-y-3" data-testid="ia-review-panel">
      <button onClick={run} disabled={loading} className="bm-btn-secondary w-full sm:w-auto" data-testid="ia-review-btn">
        <Sparkles className="w-4 h-4" /> {loading ? "Revisando… (hasta 1 min)" : "Revisar redacción con IA"}
      </button>
      <p className="text-xs text-slate-500">Solo ortografía, acentos, puntuación y concordancia. Nada se aplica solo.</p>
      {res && (
        <div className="space-y-2" data-testid="ia-review-results">
          <div className="text-xs text-slate-500">
            {res.sugerencias.length} sugerencia(s) · {res.descartadas} descartada(s) por el filtro · costo aprox. US${res.costo_usd_aprox}
          </div>
          {res.sugerencias.map((s) => (
            <div key={s.id} className="rounded-xl border border-[#E2DCD2] p-3 text-sm" data-testid={`ia-sug-${s.id}`}>
              <div className="text-red-700 line-through break-words">{s.antes}</div>
              <div className="text-emerald-700 break-words">{s.despues}</div>
              <div className="text-xs text-slate-500 mt-1">{s.motivo}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setDecision((d) => ({ ...d, [s.id]: "si" }))} data-testid={`ia-sug-aceptar-${s.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${decision[s.id] === "si" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>
                  <Check className="w-3 h-3" /> Aceptar
                </button>
                <button onClick={() => setDecision((d) => ({ ...d, [s.id]: "no" }))} data-testid={`ia-sug-rechazar-${s.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${decision[s.id] === "no" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <X className="w-3 h-3" /> Rechazar
                </button>
              </div>
            </div>
          ))}
          {aceptadas.length > 0 && (
            <button onClick={aplicar} className="bm-btn-primary w-full" data-testid="ia-apply-btn">
              Guardar {aceptadas.length} corrección(es) como versión nueva
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function VersionesPanel({ versiones, activa, onVer, onRestaurar, onValidar }) {
  return (
    <div className="bm-card p-4" data-testid="versiones-panel">
      <div className="font-heading font-bold text-slate-900 mb-2">Versiones</div>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {versiones.map((v) => (
          <div key={v.id} className="rounded-xl border border-[#E2DCD2] p-3 text-sm" data-testid={`version-${v.version}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">v{v.version}{v.version === activa && <span className="ml-2 text-[10px] uppercase text-emerald-700">en uso</span>}</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${v.estado === "validada" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {v.estado === "validada" ? "Validada" : "Pendiente de VALIDACIÓN LEGAL"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{new Date(v.creado_en).toLocaleString("es-MX")} · {v.autor_nombre}</div>
            <div className="text-xs text-slate-700 mt-1">{v.nota}</div>
            <div className="flex flex-wrap gap-3 mt-2 text-xs font-semibold text-[#1E3A5F]">
              <button onClick={() => onVer(v)} data-testid={`version-ver-${v.version}`}>Ver</button>
              <button onClick={() => onRestaurar(v)} data-testid={`version-restaurar-${v.version}`}>Regresar a esta</button>
              {v.estado !== "validada" && <button onClick={() => onValidar(v)} data-testid={`version-validar-${v.version}`}>Marcar con VALIDACIÓN LEGAL</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PreviewPanel({ bloques }) {
  return (
    <div className="bm-card p-4 sm:p-6 max-h-[70vh] overflow-y-auto text-sm leading-relaxed" data-testid="plantilla-preview">
      {bloques.map((b, i) => {
        if (b.tipo === "S") return <div key={i} className="h-3" />;
        if (b.tipo === "T") return <div key={i} className="text-center text-xs text-slate-500 my-2">[Tabla / firmas]</div>;
        const cls = b.estilo === "title_es" ? "text-center font-bold text-base" : b.estilo === "h2_es" ? "font-bold text-[#2A5C55] mt-3" : "text-justify";
        return <p key={i} className={`${cls} mb-2 whitespace-pre-line`}>{String(b.texto || "").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "")}</p>;
      })}
    </div>
  );
}
